/**
 * GlobalHeader - Primary action bar (Claude/Google pattern)
 * 
 * Layout: [Logo] [+ New Agent] | [Refine]* [Tools]* | [Bell] [Avatar]
 * * Context-aware: only visible on results/agent pages
 * 
 * Brand: Playfair Display, 700ms transitions, premium minimalism
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Settings, 
  LogOut, 
  Plus,
  Sliders,
  Download,
  Share2,
  Layers2,
  RefreshCw,
  ChevronDown,
  MoreHorizontal,
  Menu
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import StreakBadge from './StreakBadge';

export default function GlobalHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { toggleMobileSidebar } = useNavigation();
  const isMobile = useIsMobile();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const userMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);

  // Detect if we're on a results/agent page (context-aware actions)
  const isResultsPage = location.pathname.startsWith('/app/feed');
  
  // Detect public pages where header should be hidden
  const isPublicPage = location.pathname.startsWith('/share') || 
                       location.pathname.startsWith('/legal') ||
                       location.pathname.startsWith('/learn') ||
                       location.pathname === '/';

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't show on public pages or when not authenticated
  if (isPublicPage || !isAuthenticated) return null;

  // Get user initials for avatar
  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Tool actions - dispatch events that pages listen to
  const handleRefine = () => {
    window.dispatchEvent(new CustomEvent('eq:open-refine'));
  };

  const handleExport = () => {
    window.dispatchEvent(new CustomEvent('eq:export-csv'));
    setShowToolsMenu(false);
  };

  const handleShare = () => {
    window.dispatchEvent(new CustomEvent('eq:share-list'));
    setShowToolsMenu(false);
  };

  const handleBulkActions = () => {
    window.dispatchEvent(new CustomEvent('eq:toggle-bulk-actions'));
    setShowToolsMenu(false);
  };

  const handleRefresh = () => {
    window.dispatchEvent(new CustomEvent('eq:refresh-feed'));
    setShowToolsMenu(false);
  };

  // Tools menu items
  const toolsItems = [
    { id: 'export', icon: Download, label: 'Export', onClick: handleExport },
    { id: 'share', icon: Share2, label: 'Share', onClick: handleShare },
    { id: 'bulk', icon: Layers2, label: 'Bulk Actions', onClick: handleBulkActions },
    { id: 'refresh', icon: RefreshCw, label: 'Refresh', onClick: handleRefresh },
  ];

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-[9998] h-14 bg-white/95 backdrop-blur-md border-b border-black/5 transition-all duration-700 ease-out"
      style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)' }}
    >
      <div className="h-full flex items-center justify-between px-3 sm:px-4 gap-2 sm:gap-4">
        {/* Left Section: Mobile Menu + Logo + Primary Action */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <button
              onClick={toggleMobileSidebar}
              className="p-2 -ml-1 rounded-lg hover:bg-black/5 active:bg-black/10 transition-colors duration-200"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5 text-black/60" />
            </button>
          )}
          
          {/* EQ.app Logo - Real Production Asset */}
          <button
            onClick={() => navigate('/app')}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity duration-200"
          >
            <img 
              src="/favicon/favicon.svg" 
              alt="EQ.app" 
              className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
            />
            <div className="hidden sm:flex flex-col -space-y-0.5">
              <span 
                className="text-sm font-semibold text-black tracking-tight flex items-center gap-1.5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Scout
                {process.env.REACT_APP_SCOUT_EXPERIMENTAL === 'true' && (
                  <span className="text-[10px] font-medium text-[#5a20ff] bg-[#5a20ff]/10 px-1.5 py-0.5 rounded">
                    Experimental
                  </span>
                )}
              </span>
              <span className="text-[10px] text-black/40">by EQ.app</span>
            </div>
          </button>

          {/* Divider - hidden on mobile */}
          <div className="hidden sm:block w-px h-6 bg-black/10" />

          {/* New Agent - Soft Brand CTA */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Navigate to /app if not already there
              if (location.pathname !== '/app') {
                navigate('/app');
              }
              // Dispatch event to focus search input (FeedHome listens for this)
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('eq:focus-search'));
              }, 100);
            }}
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-[#5a20ff]/8 text-[#5a20ff]/80 rounded-lg text-sm font-medium hover:bg-[#5a20ff]/12 hover:text-[#5a20ff] active:scale-[0.98] transition-all duration-200 cursor-pointer"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden xs:inline sm:inline">New</span>
          </button>
        </div>

        {/* Center Section: Context-Aware Actions (Results Pages Only) */}
        {isResultsPage && (
          <div className="flex items-center gap-2">
            {/* Refine Button - Soft Brand */}
            <button
              onClick={handleRefine}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[#5a20ff]/60 hover:text-[#5a20ff]/80 hover:bg-[#5a20ff]/5 rounded-lg text-sm font-medium transition-all duration-200"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <Sliders className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Refine</span>
            </button>

            {/* Tools Dropdown */}
            <div className="relative" ref={toolsMenuRef}>
              <button
                onClick={() => setShowToolsMenu(!showToolsMenu)}
                className="flex items-center gap-1.5 px-3 py-2 text-black/60 hover:text-black hover:bg-black/5 rounded-xl text-sm font-medium transition-all duration-200"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <MoreHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">Tools</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showToolsMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Tools Dropdown Menu */}
              {showToolsMenu && (
                <div 
                  className="absolute left-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-black/5 py-1.5 z-50"
                  style={{ animation: 'eq-dropdown-in 200ms ease-out' }}
                >
                  {toolsItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={item.onClick}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-black/70 hover:bg-black/5 hover:text-black transition-colors"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        <Icon className="w-4 h-4 text-black/40" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Section: Streak + User */}
        <div className="flex items-center gap-3">
          {/* Daily Streak */}
          <StreakBadge size="sm" />

          {/* User Avatar - Soft Brand */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-8 h-8 rounded-full bg-[#5a20ff]/8 flex items-center justify-center text-[#5a20ff]/70 text-xs font-semibold hover:bg-[#5a20ff]/12 active:scale-[0.98] transition-all duration-200"
            >
              {getInitials()}
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div 
                className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-black/5 py-2 z-50"
                style={{ animation: 'eq-dropdown-in 200ms ease-out' }}
              >
                {/* User Info */}
                <div className="px-4 py-3 border-b border-black/5">
                  <p 
                    className="text-sm font-semibold text-black truncate"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-black/50 truncate">{user?.email}</p>
                </div>

                {/* Menu Items */}
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/app/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-black/70 hover:bg-black/5 hover:text-black transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  <Settings className="w-4 h-4 text-black/40" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    logout({ logoutParams: { returnTo: window.location.origin } });
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dropdown Animation Styles */}
      <style>{`
        @keyframes eq-dropdown-in {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
}
